// `toAbsoluteUrl` and `extendUrlParams` throw on unparseable input; all others return null.
function parseUrl(url: string, base?: string): URL | null {
  try {
    return new URL(url, base);
  } catch {
    return null;
  }
}

function isHttpProtocol(url: URL): boolean {
  return url.protocol === "http:" || url.protocol === "https:";
}

function toAbsoluteUrl(url: string, base: string) {
  return new URL(url, base).href;
}

function extendUrlParams(
  toUrl: string,
  fromUrl = window.location.href,
  keys?: string[],
) {
  const toUrlUrl = new URL(toUrl);
  const fromSearchParams = new URL(fromUrl).searchParams;

  (keys ?? [...fromSearchParams.keys()]).forEach((key) => {
    const value = fromSearchParams.get(key);
    if (value) {
      toUrlUrl.searchParams.set(key, value);
    }
  });

  return toUrlUrl.href;
}

// Same-origin path + search + hash, for routers that only accept paths.
function toSameOriginPath(url: string, base: string = window.location.origin) {
  const parsed = parseUrl(url, base);
  const baseOrigin = parseUrl(base)?.origin;
  if (parsed === null || parsed.origin !== baseOrigin) {
    return null;
  }
  return `${parsed.pathname}${parsed.search}${parsed.hash}`;
}

// Pathname only (no query/hash), for comparing a feature value against a location.
function toSameOriginPathname(url: string, base?: string) {
  return toSameOriginPath(url, base)?.split(/[?#]/)[0] ?? null;
}

// Pathname verbatim, trailing `/` kept: link-map keys compare as strings.
function toOriginPath(url: string, base?: string): string | null {
  const parsed = parseUrl(url, base);
  if (parsed === null || !isHttpProtocol(parsed)) {
    return null;
  }
  return `${parsed.origin}${parsed.pathname}`;
}

// null for non-http(s) or unparseable → callers must treat null as "do not follow".
function toHttpOriginPath(url: string) {
  const parsed = parseUrl(url);
  if (parsed === null || !isHttpProtocol(parsed)) {
    return null;
  }

  const pathname = parsed.pathname.replace(/(?<=.)\/$/, "");
  return `${parsed.protocol}//${parsed.host}${pathname}`;
}

// null for an unfollowable target, so it compares equal to nothing.
function withoutScheme(url: string): string | null {
  const normalized = toHttpOriginPath(url);
  return normalized === null
    ? null
    : normalized.slice(normalized.indexOf(":") + 1);
}

// Followed by an anchor click or a `Location` header → `javascript:` must not pass.
function toNavigableUrl(target: string, origin: string): string | null {
  const parsed = parseUrl(target, origin);
  return parsed === null || !isHttpProtocol(parsed) ? null : parsed.href;
}

export {
  extendUrlParams,
  isHttpProtocol,
  parseUrl,
  toAbsoluteUrl,
  toHttpOriginPath,
  toNavigableUrl,
  toOriginPath,
  toSameOriginPath,
  toSameOriginPathname,
  withoutScheme,
};
